import type { SectionState } from "../navigation";
import type { OrganizationSectionMeta } from "./organization-sections";
import { OrganizationSectionIcon } from "./organization-sections";

export interface OrganizationSectionNavCardProps<
  SectionId extends string = string,
> {
  section: OrganizationSectionMeta<SectionId>;
  statusText: string;
  state: SectionState;
  onClick: () => void;
}

export function OrganizationSectionNavCard<SectionId extends string = string>({
  section,
  statusText,
  state,
  onClick,
}: OrganizationSectionNavCardProps<SectionId>) {
  const iconBg =
    state === "complete" ? "bg-(--brand-navy)" : "bg-(--bg-canvas)";
  const iconColor =
    state === "complete" ? "text-(--fg-inverse)" : "text-(--fg-muted)";
  const ringColor =
    state === "complete"
      ? "text-(--fg-brand)"
      : state === "yellow"
        ? "text-[var(--status-warning,#f0ad00)]"
        : "text-(--fg-muted)";

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex items-center gap-3 py-3 px-3.5 rounded border",
        "bg-(--bg-white) text-left w-full",
        "border-(--border-subtle)",
        "transition-[border-color,box-shadow] duration-[120ms]",
        "hover:border-(--border-default) hover:shadow-sm",
        "focus-visible:outline-2 focus-visible:outline-(--fg-brand) focus-visible:outline-offset-2",
      ].join(" ")}
    >
      <span
        className={[
          "inline-flex items-center justify-center",
          "w-[36px] h-[36px] rounded-[3px] shrink-0",
          iconBg,
          iconColor,
        ].join(" ")}
        aria-hidden
      >
        <OrganizationSectionIcon icon={section.icon} />
      </span>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-[15px] font-bold tracking-[-0.015em] text-(--fg-primary) leading-[1.2] truncate">
          {section.overviewLabel ?? section.title}
        </span>
        <span className="text-[11.5px] text-(--fg-secondary) tabular-nums truncate">
          {statusText}
        </span>
      </div>
      <span
        className={[
          "flex items-center justify-center w-[24px] h-[24px] shrink-0",
          ringColor,
        ].join(" ")}
        aria-hidden
      >
        {state === "complete" ? (
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12.5l4 4 10-10" />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4L22 20H2L12 4Z" />
            <path d="M12 11v3" />
            <path d="M12 17h.01" />
          </svg>
        )}
      </span>
    </button>
  );
}
